import React, { ReactNode, useMemo } from 'react'

import { Button, ButtonProps } from 'antd'

import Icon from '../govwell-ui/components/Icon'
import { IconKey } from '../govwell-ui/components/Icon/util'
import Menu from '../govwell-ui/components/Menu/Menu'
import Tooltip from '../govwell-ui/components/Tooltip'
import { ActionItem, RowLevelAction } from '../govwell-ui/types'
import { RefetchFnType } from '../types'

import useDisclosure from './use-disclosure'

export interface UseDropdownParams {
  refetch: RefetchFnType
}

export interface DropdownModalProps {
  onClose: () => void
  refetch: RefetchFnType
}

type Params<TKey extends string = string, TData = unknown> = {
  label: ActionItem['label']
  key: ActionItem<TKey>['key']
  icon: IconKey
  isDisabled?: boolean
  isDestructive?: boolean
  refetch: RefetchFnType
  tooltip?: React.ReactNode
} & (
  | {
      onClick: (data?: TData[]) => void
      renderModal?: never
    }
  | {
      onClick?: never
      renderModal: (props: DropdownModalProps) => ReactNode
    }
)

export const useMakeActionItem = <TKey extends string = string, TData = unknown>(
  params: Params<TKey, TData>
) => {
  const { label, key, icon, isDisabled, isDestructive, renderModal, onClick, refetch, tooltip } =
    params
  const modalState = useDisclosure()

  return useMemo<RowLevelAction<TKey>>(() => {
    const actionItem: ActionItem<TKey> = {
      disabled: isDisabled,
      isDestructive,
      label,
      key,
      icon,
      tooltip,
      onClick: (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation()
        e.preventDefault()
        onClick?.()
        // Defer so the table Actions menu can close before mounting a dialog (avoids a
        // full-screen pointer blocker in #modal-root that survives until refresh).
        requestAnimationFrame(() => {
          modalState?.open()
        })
      },
    }

    return {
      actionItem,
      modal: (
        <React.Fragment key={key}>
          {modalState.isOpen ? renderModal?.({ onClose: modalState.close, refetch }) : null}
        </React.Fragment>
      ),
    }
  }, [
    icon,
    isDestructive,
    isDisabled,
    key,
    label,
    modalState,
    onClick,
    refetch,
    renderModal,
    tooltip,
  ])
}

export const renderActionItemAsButton = <TKey extends string = string>(
  item: ActionItem<TKey>,
  buttonProps?: ButtonProps
) => {
  const { icon, disabled, label, onClick, tooltip, key } = item
  const button = (
    <Button
      key={key}
      size="small"
      onClick={onClick}
      icon={<Icon icon={icon} />}
      disabled={disabled}
      {...buttonProps}
    >
      {label}
    </Button>
  )
  if (tooltip) {
    return (
      <Tooltip key={key} content={tooltip}>
        {button}
      </Tooltip>
    )
  }
  return button
}

export const renderActionItemAsMenuItem = <TKey extends string = string>(
  item: ActionItem<TKey>
) => {
  const { disabled, icon, isDestructive, key, label, tooltip, ...rest } = item
  return (
    <Menu.Item
      key={key}
      icon={icon}
      isDestructive={isDestructive}
      isDisabled={disabled}
      tooltip={tooltip}
      {...rest}
    >
      {label}
    </Menu.Item>
  )
}
